import { useEffect, useState } from "react";
import { fetchTableCount } from "../../api/table";
import { useAppIdMap } from "../app";
import { getPages } from "../../api/page";
import { DatabasePageData, NotebookPage } from "../../types/note";
import {
  useAddNotebookPagesToState,
  useSelectedNotebookPageIdMap,
} from "../page";
import { useFetchDatabasePropertyValues } from "../page/property";
import { queryViewPages } from "../../api/page/viewQuery";
import { useSelectedDatabaseViewId } from "../page/view";
import { useViewSetting } from "../page/viewSetting";

export const useDatabaseTableSearch = (tableId: string) => {
  const [index, setIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const { appId } = useAppIdMap();
  const [tableCount, setTableCount] = useState(0);
  const [pages, setPages] = useState<NotebookPage[]>([]);
  const addNotebookPagesToState = useAddNotebookPagesToState();
  const fetchDatabasePropertyValues = useFetchDatabasePropertyValues();
  const [selectedViewId] = useSelectedDatabaseViewId();
  const setting = useViewSetting(selectedViewId);
  const addPage = (page: NotebookPage) => {
    setPages([...pages, page]);
  };
  const pageSize = 10;

  const fetchTable = async () => {
    if (!appId) return;
    if (!tableId) return;
    const skip = pageSize * index;
    const limit = pageSize;
    setIsLoading(true);

    if (selectedViewId) {
      try {
        const res = await queryViewPages({ "view-id": selectedViewId, skip, limit });
        setPages(res.pages);
        setTableCount(res.count);
        addNotebookPagesToState(res.pages);
        fetchDatabasePropertyValues({
          folderId: tableId,
          pageIds: res.pages.map((page) => page["page-id"]),
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      const countPromise = fetchTableCount({ appId: appId, tableId }).then(
        (res) => {
          setTableCount(res.count);
        }
      );
      const rowPromise = getPages({
        "app-id": appId!,
        "folder-id": tableId,
        skip,
        limit,
        "no-page-data": true,
      }).then((getPagesRes) => {
        const { pages } = getPagesRes;
        setPages(pages);
        addNotebookPagesToState(pages);
        fetchDatabasePropertyValues({
          folderId: tableId,
          pageIds: pages.map((page) => page["page-id"]),
        });
      });
      await Promise.all([countPromise, rowPromise]).finally(() => {
        setIsLoading(false);
      });
    }
  };

  useEffect(() => {
    if (!appId || !tableId) return;

    fetchTable();
  }, [appId, tableId, index, selectedViewId, JSON.stringify(setting?.sort), JSON.stringify(setting?.filter)]);

  return {
    fetchTable,
    isLoading,
    index,
    setIndex,
    tableCount,
    pages,
    pageSize,
    addPage,
  };
};

export const useFetchDatabasePagesListener = () => {
  const { selectedNotebookPageId: tableId } = useSelectedNotebookPageIdMap();
  const { appId } = useAppIdMap();
  const addPagesToState = useAddNotebookPagesToState();

  useEffect(() => {
    if (!appId) return;
    if (!tableId) return;
    getPages({ "app-id": appId!, "folder-id": tableId }).then((result) => {
      const pages = result.pages as NotebookPage[];
      addPagesToState(pages);
    });
  }, [appId, tableId]);
};
